// frontend/src/components/CrearEditarClaseRecursoModal.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { FiX, FiSave } from 'react-icons/fi';
import { claseRecursoService, planCuentaService } from '../services/onebaseService';
import './CrearModal.css';

const CrearEditarClaseRecursoModal = ({ clase, padreInicial, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [clasesPadre, setClasesPadre] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    parent: '',
    cuenta_imputable: '',
    activo: true
  });

  useEffect(() => {
    cargarDatos();
    if (clase) {
      setFormData({
        codigo: clase.codigo || '',
        nombre: clase.nombre || '',
        descripcion: clase.descripcion || '',
        parent: clase.parent || '',
        cuenta_imputable: clase.cuenta_imputable || '',
        activo: clase.activo !== undefined ? clase.activo : true
      });
    } else if (padreInicial) {
      setFormData(prev => ({ ...prev, parent: padreInicial.id }));
    }
  }, [clase, padreInicial]);

  const cargarDatos = async () => {
    try {
      // Cargar clases que pueden ser padre (nivel < 3)
      const clasesRes = await claseRecursoService.getAll({ status: 'activo' });
      const clasesData = clasesRes.data?.results || clasesRes.data || [];
      // Filtrar solo las que pueden tener hijos (nivel < 3)
      const clasesPuedeTenerHijos = clasesData.filter(c => c.puede_tener_hijos || c.nivel < 3);
      setClasesPadre(clasesPuedeTenerHijos);

      // Cargar plan de cuentas (solo cuentas imputables)
      const cuentasRes = await planCuentaService.getAll({ imputa: 'true', activo: 'true' });
      const cuentasData = cuentasRes.data?.results || cuentasRes.data || [];
      setPlanCuentas(cuentasData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos del formulario');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calcularNivel = () => {
    if (!formData.parent) return 1;
    const padre = clasesPadre.find(c => c.id === parseInt(formData.parent));
    return padre ? (padre.nivel_actual || padre.nivel) + 1 : 1;
  };

  const esNivel3 = () => {
    return calcularNivel() === 3;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        codigo: formData.codigo,
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        parent: formData.parent ? parseInt(formData.parent) : null,
        activo: formData.activo
      };

      // Solo enviar cuenta_imputable si es nivel 3 y tiene un valor
      if (esNivel3() && formData.cuenta_imputable) {
        dataToSend.cuenta_imputable = parseInt(formData.cuenta_imputable);
      }

      if (clase) {
        // Actualizar clase existente
        await claseRecursoService.update(clase.id, dataToSend);
        toast.success('Clase de recurso actualizada correctamente');
      } else {
        // Crear nueva clase
        await claseRecursoService.create(dataToSend);
        toast.success('Clase de recurso creada correctamente');
      }
      onSuccess();
    } catch (error) {
      console.error('Error al guardar clase:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail ||
                      (clase ? 'Error al actualizar la clase' : 'Error al crear la clase');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>{clase ? 'Editar Clase de Recurso' : 'Nueva Clase de Recurso'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <div className="alert-info" style={{
              backgroundColor: '#e0e7ff',
              border: '1px solid #69B32D',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <strong>Nivel actual: {calcularNivel()}</strong>
              {esNivel3() && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  ✓ Esta clase será imputable y deberá tener una cuenta asociada
                </div>
              )}
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
              <div className="form-group">
                <label>Código *</label>
                <input
                  type="text"
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleInputChange}
                  required
                  placeholder="1, 2, 3..."
                />
              </div>

              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  placeholder="Nombre de la clase"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                rows="3"
                placeholder="Descripción de la clase de recurso"
              />
            </div>

            <div className="form-group">
              <label>Clase Padre</label>
              <select
                name="parent"
                value={formData.parent}
                onChange={handleInputChange}
              >
                <option value="">Sin padre (Nivel 1)</option>
                {clasesPadre.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.codigo_completo || c.codigo} - {c.nombre} (Nivel {c.nivel_actual || c.nivel})
                  </option>
                ))}
              </select>
              <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                Solo puede seleccionar clases de nivel 1 o 2
              </small>
            </div>

            {esNivel3() && (
              <div className="form-group">
                <label>Cuenta Imputable {esNivel3() ? '*' : ''}</label>
                <select
                  name="cuenta_imputable"
                  value={formData.cuenta_imputable}
                  onChange={handleInputChange}
                  required={esNivel3()}
                >
                  <option value="">Seleccionar cuenta...</option>
                  {planCuentas.map(cuenta => (
                    <option key={cuenta.id} value={cuenta.id}>
                      {cuenta.codigo_completo || cuenta.codigo} - {cuenta.nombre}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Solo clases de nivel 3 pueden tener una cuenta imputable asociada
                </small>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo}
                  onChange={handleInputChange}
                />
                Activo
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              <FiSave /> {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CrearEditarClaseRecursoModal;
